/**
 * DOM query helpers for checkJs-friendly element handling.
 * Avoid inline JSDoc type-cast comments in feature modules.
 */

type ElementCtor<T extends Element> = abstract new (...args: never[]) => T;
type QuerySource = ParentNode | Event | EventTarget | Element | null | undefined;

export class DomQuery {
    private static callQuerySelector(root: object, selector: string): Element | null {
        const query = Reflect.get(root, "querySelector");
        return typeof query === "function"
            ? (query as (this: object, selector: string) => Element | null).call(root, selector)
            : null;
    }

    private static callQuerySelectorAll(root: object, selector: string): Element[] {
        const queryAll = Reflect.get(root, "querySelectorAll");
        if (typeof queryAll !== "function") return [];
        return Array.from((queryAll as (this: object, selector: string) => NodeListOf<Element>).call(root, selector));
    }

    private static callClosest(root: Element, selector: string): Element | null {
        const closestFn = Reflect.get(root, "closest");
        return typeof closestFn === "function"
            ? (closestFn as (this: Element, selector: string) => Element | null).call(root, selector)
            : null;
    }

    public static isInstanceOf<T extends Element = HTMLElement>(
        element: Element | null | undefined,
        elementClass?: ElementCtor<T>,
    ): element is T {
        switch (true) {
            case !element:
                return false;
            case !!elementClass:
                return element instanceof elementClass;
            case element instanceof HTMLFormElement:
                return true as boolean;
            case element instanceof HTMLElement:
                return true as boolean;
            default:
                return false;
        }
    }

    public static getElement<T extends Element = HTMLElement>(
        source: QuerySource,
        selectorOrElementClass?: string | ElementCtor<T>,
        elementClass?: ElementCtor<T>,
    ): T | null {
        let selector: string | null = null;
        let expectedClass = elementClass;

        if (typeof selectorOrElementClass === "string") {
            selector = selectorOrElementClass;
        } else if (typeof selectorOrElementClass === "function") {
            expectedClass = selectorOrElementClass;
        }

        let baseElement: Element | null = null;

        switch (true) {
            case source instanceof Element:
                baseElement = source;
                break;
            case source instanceof Event:
                baseElement = source.target instanceof Element ? source.target : null;
                break;
            case !!(source && typeof source === "object" && "target" in source):
                baseElement = source.target instanceof Element ? source.target : null;
                break;
            default:
                baseElement = null;
                break;
        }

        let found: Element | null = null;

        if (selector) {
            if (baseElement && typeof baseElement.querySelector === "function") {
                found = DomQuery.callQuerySelector(baseElement, selector);
            } else if (
                source &&
                typeof source === "object" &&
                "querySelector" in source &&
                typeof source.querySelector === "function"
            ) {
                found = DomQuery.callQuerySelector(source, selector);
            }
        } else {
            found = baseElement;
            const expectsHtmlForm = typeof expectedClass === "function" && expectedClass.name === "HTMLFormElement";
            if (expectsHtmlForm && found instanceof Element && !(found instanceof HTMLFormElement)) {
                found = DomQuery.callClosest(found, "form");
            }
        }

        return DomQuery.isInstanceOf(found, expectedClass) ? found : null;
    }

    public static getForm(source: QuerySource, selector?: string): HTMLFormElement | null {
        if (source instanceof HTMLFormElement && !selector) {
            return source;
        }

        if (selector) {
            return DomQuery.getElement(source, selector, HTMLFormElement);
        }

        if (source instanceof Event || !!(source && typeof source === "object" && "target" in source)) {
            return DomQuery.getElement(source, HTMLFormElement);
        }

        return DomQuery.getElement(source, "form", HTMLFormElement);
    }

    public static getButton(source: QuerySource, selector?: string): HTMLButtonElement | null {
        if (source instanceof HTMLButtonElement && !selector) {
            return source;
        }

        if (selector) {
            return DomQuery.getElement(source, selector, HTMLButtonElement);
        }

        if (source instanceof Event || !!(source && typeof source === "object" && "target" in source)) {
            return DomQuery.getElement(source, HTMLButtonElement);
        }

        return DomQuery.getElement(source, "button", HTMLButtonElement);
    }

    public static getClosestForm(source: Event | EventTarget | Element | null | undefined): HTMLFormElement | null {
        return DomQuery.closest(source, "form", HTMLFormElement);
    }

    public static getInput(source: QuerySource, selector?: string): HTMLInputElement | null {
        if (source instanceof HTMLInputElement && !selector) {
            return source;
        }

        if (selector) {
            return DomQuery.getElement(source, selector, HTMLInputElement);
        }

        if (source instanceof Event || !!(source && typeof source === "object" && "target" in source)) {
            return DomQuery.getElement(source, HTMLInputElement);
        }

        return DomQuery.getElement(source, "input", HTMLInputElement);
    }

    public static getTextarea(source: QuerySource, selector?: string): HTMLTextAreaElement | null {
        if (source instanceof HTMLTextAreaElement && !selector) {
            return source;
        }

        if (selector) {
            return DomQuery.getElement(source, selector, HTMLTextAreaElement);
        }

        if (source instanceof Event || !!(source && typeof source === "object" && "target" in source)) {
            return DomQuery.getElement(source, HTMLTextAreaElement);
        }

        return DomQuery.getElement(source, "textarea", HTMLTextAreaElement);
    }

    public static getSelect(source: QuerySource, selector?: string): HTMLSelectElement | null {
        if (source instanceof HTMLSelectElement && !selector) {
            return source;
        }

        if (selector) {
            return DomQuery.getElement(source, selector, HTMLSelectElement);
        }

        if (source instanceof Event || !!(source && typeof source === "object" && "target" in source)) {
            return DomQuery.getElement(source, HTMLSelectElement);
        }

        return DomQuery.getElement(source, "select", HTMLSelectElement);
    }

    public static queryElement<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: ElementCtor<T>,
    ): T | null {
        return DomQuery.getElement(root, selector, elementClass);
    }

    public static queryElements<T extends Element = HTMLElement>(
        root: ParentNode | null | undefined,
        selector: string,
        elementClass?: ElementCtor<T>,
    ): T[] {
        if (!root || typeof root.querySelectorAll !== "function") return [];
        const matched = DomQuery.callQuerySelectorAll(root, selector);
        return matched.filter((el): el is T => DomQuery.isInstanceOf(el, elementClass));
    }

    public static fromEventTarget<T extends Element = HTMLElement>(
        eventOrTarget: Event | EventTarget | Element | null | undefined,
        elementClass?: ElementCtor<T>,
    ): T | null {
        return DomQuery.getElement(eventOrTarget, elementClass);
    }

    public static closest<T extends Element = HTMLElement>(
        elementOrEventTarget: Event | EventTarget | Element | null | undefined,
        selector: string,
        elementClass?: ElementCtor<T>,
    ): T | null {
        const base = DomQuery.getElement(elementOrEventTarget, Element);
        if (!base || typeof base.closest !== "function") return null;
        const found = DomQuery.callClosest(base, selector);
        if (!found) return null;
        return DomQuery.isInstanceOf(found, elementClass) ? found : null;
    }

    public static queryHTMLElement(root: ParentNode | null | undefined, selector: string): HTMLElement | null {
        return DomQuery.queryElement(root, selector);
    }

    public static queryAllHTMLElements(root: ParentNode | null | undefined, selector: string): HTMLElement[] {
        return DomQuery.queryElements(root, selector);
    }

    public static findOrNull<T>(items: T[] | null | undefined, predicate: (item: T) => boolean): T | null {
        if (!Array.isArray(items)) return null;
        const found = items.find(predicate);
        return found ?? null;
    }
}

export function queryElement<T extends Element = HTMLElement>(
    root: ParentNode | null | undefined,
    selector: string,
    elementClass?: ElementCtor<T>,
): T | null {
    return DomQuery.queryElement(root, selector, elementClass);
}

export function getElement<T extends Element = HTMLElement>(
    source: QuerySource,
    selectorOrElementClass?: string | ElementCtor<T>,
    elementClass?: ElementCtor<T>,
): T | null {
    return DomQuery.getElement(source, selectorOrElementClass, elementClass);
}

export function getForm(source: QuerySource, selector?: string): HTMLFormElement | null {
    return DomQuery.getForm(source, selector);
}

export function getButton(source: QuerySource, selector?: string): HTMLButtonElement | null {
    return DomQuery.getButton(source, selector);
}

export function getClosestForm(source: Event | EventTarget | Element | null | undefined): HTMLFormElement | null {
    return DomQuery.getClosestForm(source);
}

export function getInput(source: QuerySource, selector?: string): HTMLInputElement | null {
    return DomQuery.getInput(source, selector);
}

export function getTextarea(source: QuerySource, selector?: string): HTMLTextAreaElement | null {
    return DomQuery.getTextarea(source, selector);
}

export function getSelect(source: QuerySource, selector?: string): HTMLSelectElement | null {
    return DomQuery.getSelect(source, selector);
}

export function queryElements<T extends Element = HTMLElement>(
    root: ParentNode | null | undefined,
    selector: string,
    elementClass?: ElementCtor<T>,
): T[] {
    return DomQuery.queryElements(root, selector, elementClass);
}

export function fromEventTarget<T extends Element = HTMLElement>(
    eventOrTarget: Event | EventTarget | Element | null | undefined,
    elementClass?: ElementCtor<T>,
): T | null {
    return DomQuery.fromEventTarget(eventOrTarget, elementClass);
}

export function closest<T extends Element = HTMLElement>(
    elementOrEventTarget: Event | EventTarget | Element | null | undefined,
    selector: string,
    elementClass?: ElementCtor<T>,
): T | null {
    return DomQuery.closest(elementOrEventTarget, selector, elementClass);
}

export function queryHTMLElement(root: ParentNode | null | undefined, selector: string): HTMLElement | null {
    return DomQuery.queryHTMLElement(root, selector);
}

export function queryAllHTMLElements(root: ParentNode | null | undefined, selector: string): HTMLElement[] {
    return DomQuery.queryAllHTMLElements(root, selector);
}

export function findOrNull<T>(items: T[] | null | undefined, predicate: (item: T) => boolean): T | null {
    return DomQuery.findOrNull(items, predicate);
}
